import { useState } from 'react'
import { useList } from '@refinedev/core'
import { Input } from '@/components/ui/input'
import { useDebounce, cn } from '@/lib/utils'
import type { FkMapping } from '@/resources/fkMappings'

interface FkSelectProps {
  fieldName: string
  mapping: FkMapping
  value: string | null
  onChange: (id: string) => void
  disabled?: boolean
  filter?: { field: string; value: string }
}

function getLabel(record: Record<string, unknown>, mapping: FkMapping): string {
  if (mapping.formatLabel) {
    try {
      return mapping.formatLabel(record)
    } catch {
      // fallback to labelField
    }
  }
  return String(record[mapping.labelField] ?? record.id ?? '')
}

function buildSelectClause(mapping: FkMapping): string | undefined {
  if (mapping.embeddedSelect) return mapping.embeddedSelect
  if (mapping.extraFields?.length) {
    return `id,${mapping.labelField},${mapping.extraFields.join(',')}`
  }
  return undefined
}

export function FkSelect({ mapping, value, onChange, disabled, filter }: FkSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const searchField = mapping.searchField ?? mapping.labelField

  const selectClause = buildSelectClause(mapping)

  const { data, isLoading } = useList({
    resource: mapping.resource,
    pagination: { pageSize: 100 },
    filters: [
      ...(debouncedSearch
        ? [{ field: searchField, operator: 'contains' as const, value: debouncedSearch }]
        : []),
      ...(filter
        ? [{ field: filter.field, operator: 'eq' as const, value: filter.value }]
        : []),
    ],
    meta: selectClause ? { select: selectClause } : undefined,
  })

  const options = (data?.data ?? []) as Record<string, unknown>[]
  const selectedOption = options.find((o) => String(o.id) === String(value))

  const displayValue = selectedOption
    ? getLabel(selectedOption, mapping)
    : value ?? ''

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center justify-between border rounded-lg px-3 py-2 text-sm text-left',
          'bg-slate-50 dark:bg-slate-700/50',
          'hover:bg-slate-100 dark:hover:bg-slate-700/70 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#076698]/40',
          open
            ? 'border-[#076698]/60 ring-2 ring-[#076698]/20'
            : 'border-input dark:border-slate-600/60'
        )}
      >
        <span className={cn('truncate', displayValue ? 'text-foreground' : 'text-muted-foreground')}>
          {displayValue || `Select ${mapping.resource}...`}
        </span>
        <svg
          className="w-3 h-3 text-muted-foreground shrink-0 ml-2"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 top-full mt-1 left-0',
            'w-[min(100vw-2rem,28rem)] max-w-[min(100vw-2rem,28rem)]',
            'glass-card rounded-xl border border-white/70 dark:border-white/10 shadow-xl overflow-hidden'
          )}
        >
          <div className="p-2 border-b border-border/30 dark:border-white/10">
            <Input
              autoFocus
              placeholder={`Search ${mapping.resource}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">No results</div>
            ) : (
              options.map((opt) => (
                <button
                  key={String(opt.id)}
                  type="button"
                  onClick={() => {
                    onChange(String(opt.id))
                    setOpen(false)
                    setSearch('')
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors',
                    String(opt.id) === String(value) && 'bg-[#e0f0f7] text-[#076698]'
                  )}
                >
                  {getLabel(opt, mapping)}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
